import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Collapse,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Chip
} from '@mui/material';
import {
  MonetizationOn as MoneyIcon,
  ShoppingCart as AffiliateIcon,
  Campaign as SponsoredIcon,
  Info as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';

function Step8Monetization({ monetizationStrategy, amazonTag, adsenseId, onChange }) {
  const [selectedStrategies, setSelectedStrategies] = useState(monetizationStrategy || []);
  const [showInfo, setShowInfo] = useState(true);

  const monetizationOptions = [
    {
      id: 'affiliate_links',
      label: 'Affiliate Marketing',
      icon: <AffiliateIcon />,
      description: 'Earn commissions by recommending products',
      extraField: {
        name: 'amazonTag',
        label: 'Amazon Affiliate Tag',
        placeholder: 'yourtag-20',
        value: amazonTag,
        helperText: 'Enter your Amazon Associates tracking ID'
      }
    },
    {
      id: 'ads',
      label: 'Display Advertising',
      icon: <MoneyIcon />,
      description: 'Generate revenue through ad placements',
      extraField: {
        name: 'adsenseId',
        label: 'Google AdSense ID',
        placeholder: 'pub-xxxxxxxxxxxxxxxx',
        value: adsenseId,
        helperText: 'Enter your Google AdSense Publisher ID'
      }
    },
    {
      id: 'sponsored_posts',
      label: 'Sponsored Content',
      icon: <SponsoredIcon />,
      description: 'Collaborate with brands for sponsored articles'
    },
    {
      id: 'digital_products',
      label: 'Digital Products',
      icon: <MoneyIcon />,
      description: 'Sell ebooks, courses, or downloadable content'
    }
  ];

  const handleStrategyToggle = (strategyId) => {
    let newStrategies;
    if (selectedStrategies.includes(strategyId)) {
      newStrategies = selectedStrategies.filter(s => s !== strategyId);
    } else {
      newStrategies = [...selectedStrategies, strategyId];
    }
    setSelectedStrategies(newStrategies);
    onChange({ monetizationStrategy: newStrategies });
  };

  const handleExtraFieldChange = (field, value) => {
    onChange({ [field]: value });
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Step 8: Monetization Strategy
      </Typography>

      <Collapse in={showInfo}>
        <Alert 
          severity="info"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setShowInfo(false)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{ mb: 2 }}
        >
          Select the ways you'd like to monetize your blog. You can combine multiple strategies.
        </Alert>
      </Collapse>

      {/* Main Selection Area */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          {monetizationOptions.map((option) => (
            <Box key={option.id}>
              <Chip
                icon={option.icon}
                label={option.label}
                onClick={() => handleStrategyToggle(option.id)}
                variant={selectedStrategies.includes(option.id) ? "filled" : "outlined"}
                color={selectedStrategies.includes(option.id) ? "primary" : "default"}
                sx={{
                  width: '100%',
                  justifyContent: 'flex-start',
                  height: 'auto',
                  py: 1,
                  '& .MuiChip-label': {
                    display: 'flex',
                    flexDirection: 'column',
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ pl: 2, mt: 0.5, display: 'block' }}>
                {option.description}
              </Typography>

              {/* Extra Fields for Selected Options */}
              {selectedStrategies.includes(option.id) && option.extraField && (
                <TextField
                  size="small"
                  label={option.extraField.label}
                  placeholder={option.extraField.placeholder}
                  value={option.extraField.value || ''}
                  onChange={(e) => handleExtraFieldChange(option.extraField.name, e.target.value)}
                  helperText={option.extraField.helperText}
                  fullWidth
                  sx={{ mt: 1, ml: 2, width: 'calc(100% - 16px)' }}
                />
              )}
            </Box>
          ))}
        </Stack>
      </Paper>

      {/* Tips Section */}
      <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon fontSize="small" />
          Tips for Monetization:
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
          <Chip size="small" label="Start with 1-2 methods" />
          <Chip size="small" label="Focus on value first" />
          <Chip size="small" label="Be transparent" />
          <Chip size="small" label="Test different approaches" />
        </Stack>
      </Paper>
    </Box>
  );
}

export default Step8Monetization;
